import React, { useState, useMemo, useRef, useCallback, memo } from 'react';
import { motion, MotionConfig } from 'framer-motion';
import {
  FaJava, FaReact, FaNodeJs, FaDatabase, FaDocker, FaGitAlt, FaHtml5, FaCss3Alt,
  FaJs, FaCode, FaServer, FaPython, FaFlask, FaBrain, FaChartLine, FaCloud,
  FaExchangeAlt, FaMagic, FaProjectDiagram, FaAws,
  FaMapMarkedAlt, FaHandHoldingHeart, FaHospital
} from 'react-icons/fa';
import {
  SiSpringboot, SiMysql, SiMongodb, SiRedis, SiExpress, SiPostman,
  SiTypescript, SiTailwindcss, SiBootstrap, SiPostgresql,
  SiIntellijidea, SiVscodium, SiVercel
} from 'react-icons/si';
import './Skills.css';

/* ------------------------------------------------------------------ */
/*  PROJECT NAMES                                                     */
/* ------------------------------------------------------------------ */
const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

const NAMES = {
  bhoomiseva: 'Bhoomi Seva',
  majhiyojna: 'Majhi-Yojna',
  majhiyojana: 'Majhi-Yojna',
  medibook: 'MediBook Pro',
  medibookpro: 'MediBook Pro',
  shopsmart: 'ShopSmart',
  bankify: 'Bankify',
  taskflow: 'TaskFlow',
  fooddelivery: 'Food Delivery',
  ebook: 'E-Book',
  guessnumber: 'Guess Number',
  portfolio: 'Portfolio',
  poftfolio: 'Portfolio'
};

const u = (s) =>
  s.split(',')
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => NAMES[norm(x)] || x);

const ALL = 'Bhoomi-seva, Majhi-Yojna, Medibook, Shopsmart, Bankify, Taskflow, FoodDelivery, E-Book, GuessNumber';
const API7 = 'Bhoomi-seva, Majhi-Yojna, Medibook, Shopsmart, Bankify, Taskflow, FoodDelivery';

/* ------------------------------------------------------------------ */
/*  SKILLS DATA                                                        */
/* ------------------------------------------------------------------ */
const RAW = [
  // ----- BACKEND -----
  { name: 'Java', category: 'backend', Icon: FaJava, color: '#ED8B00',
    description: 'Main backend language. OOP, collections, streams and exception handling behind every Spring Boot API.',
    used: u(ALL) },
  { name: 'Spring Boot', category: 'backend', Icon: SiSpringboot, color: '#6DB33F',
    description: 'Backend APIs for my live projects: controllers, services, Spring Data JPA and layered architecture.',
    used: u(ALL) },
  { name: 'Spring Security', category: 'backend', Icon: SiSpringboot, color: '#6DB33F',
    description: 'JWT login and role-based access control to protect API endpoints.',
    used: u(ALL) },
  { name: 'REST APIs', category: 'backend', Icon: FaExchangeAlt, color: '#64FFDA',
    description: 'REST endpoints between React and Spring Boot, with request validation and clear error responses.',
    used: u('Bhoomi-seva, Majhi-Yojna, Medibook, Bankify, Taskflow') },
  { name: 'Python', category: 'backend', Icon: FaPython, color: '#3776AB',
    description: 'Machine learning code and services: XGBoost land valuation and cosine-similarity scheme ranking.',
    used: u('Bhoomi-seva, Majhi-Yojna') },
  { name: 'Flask', category: 'backend', Icon: FaFlask, color: '#E5E5E5',
    description: 'Serves the scheme recommendation model as an API.',
    used: u('Bhoomi-seva, Majhi-Yojna') },
  { name: 'Node.js', category: 'backend', Icon: FaNodeJs, color: '#339933',
    description: 'JavaScript runtime for building REST APIs with Express.',
    used: u(API7) },
  { name: 'Express.js', category: 'backend', Icon: SiExpress, color: '#E5E5E5',
    description: 'Routing, middleware and error handling for Node.js APIs.',
    used: u(API7) },
  { name: 'Microservices', category: 'backend', Icon: FaServer, color: '#FF6B6B',
    description: 'Service-based architecture with Spring Boot: independent services, API gateway, service discovery and REST calls between services.',
    used: u('Medibook, Shopsmart') },

  // ----- FRONTEND -----
  { name: 'React.js', category: 'frontend', Icon: FaReact, color: '#61DAFB',
    description: 'UI for all my live projects: components, hooks, React Router and API integration.',
    used: u(ALL) },
  { name: 'JavaScript', category: 'frontend', Icon: FaJs, color: '#F7DF1E',
    description: 'ES6+, async/await and fetch to connect the frontend with backend APIs.',
    used: u(ALL) },
  { name: 'HTML5', category: 'frontend', Icon: FaHtml5, color: '#E34F26',
    description: 'Semantic markup and forms across every project.',
    used: u(ALL) },
  { name: 'CSS3', category: 'frontend', Icon: FaCss3Alt, color: '#1572B6',
    description: 'Responsive layouts with Flexbox and Grid, animations, and dark / light themes.',
    used: u(ALL) },
  { name: 'Framer Motion', category: 'frontend', Icon: FaMagic, color: '#F472B6',
    description: 'Page transitions, scroll reveals and hover effects.',
    used: u('Portfolio') },
  { name: 'TypeScript', category: 'frontend', Icon: SiTypescript, color: '#3178C6',
    description: 'Typed JavaScript for React: interfaces, generics and safer refactoring.',
    used: u(ALL) },
  { name: 'Tailwind CSS', category: 'frontend', Icon: SiTailwindcss, color: '#06B6D4',
    description: 'Utility-first styling for fast, responsive layouts with dark mode support.',
    used: u(ALL) },
  { name: 'Bootstrap', category: 'frontend', Icon: SiBootstrap, color: '#7952B3',
    description: 'Grid system and ready-made components for quick responsive pages.',
    used: u('FoodDelivery, E-Book, GuessNumber') },

  // ----- AI / ML -----
  { name: 'Machine Learning', category: 'ai', Icon: FaBrain, color: '#F97316',
    description: 'Models served inside web apps: land valuation and government scheme recommendation.',
    used: u('Bhoomi-seva, Majhi-Yojna') },
  { name: 'XGBoost', category: 'ai', Icon: FaChartLine, color: '#00D9A5',
    description: 'Gradient boosting model that estimates land value.',
    used: u('Bhoomi-seva') },
  { name: 'Cosine Similarity', category: 'ai', Icon: FaProjectDiagram, color: '#A78BFA',
    description: 'Ranks government schemes against a citizen profile after rule-based filtering.',
    used: u('Majhi-Yojna') },

  // ----- DATABASE -----
  { name: 'MySQL', category: 'database', Icon: SiMysql, color: '#4479A1',
    description: 'Relational schemas, joins and indexing for appointments and land records.',
    used: u('Bhoomi-seva, Majhi-Yojna') },
  { name: 'TiDB Cloud', category: 'database', Icon: FaDatabase, color: '#DC150B',
    description: 'MySQL-compatible cloud database that hosts the live project data.',
    used: u('Bhoomi-seva, Majhi-Yojna') },
  { name: 'MongoDB', category: 'database', Icon: SiMongodb, color: '#47A248',
    description: 'Document database with Mongoose for Node.js APIs.',
    used: u('Shopsmart, Bankify, Taskflow, FoodDelivery') },
  { name: 'Redis', category: 'database', Icon: SiRedis, color: '#DC382D',
    description: 'Caching layer to speed up frequent reads.',
    used: u('MediBook') },
  { name: 'PostgreSQL', category: 'database', Icon: SiPostgresql, color: '#4169E1',
    description: 'Relational database with advanced SQL: joins, window functions, CTEs and JSONB.',
    used: u('MediBook') },

  // ----- DEVOPS & TOOLS -----
  { name: 'Docker', category: 'devops', Icon: FaDocker, color: '#2496ED',
    description: 'Containerizing apps with Docker and Docker Compose.',
    used: u('Shopsmart, FoodDelivery') },
  { name: 'Git & GitHub', category: 'devops', Icon: FaGitAlt, color: '#F05032',
    description: 'Version control for every project: branches, commits and pull requests.',
    used: u(ALL) },
  { name: 'Postman', category: 'devops', Icon: SiPostman, color: '#FF6C37',
    description: 'Testing REST endpoints and saving request collections before wiring the frontend.',
    used: u(API7) },
  { name: 'Render', category: 'devops', Icon: FaCloud, color: '#46E3B7',
    description: 'Hosts the React frontends, Spring Boot APIs and Python services of my live projects.',
    used: u(ALL) },
  { name: 'Vercel', category: 'devops', Icon: SiVercel, color: '#E5E5E5',
    description: 'Hosts React frontends with automatic deploys from GitHub on every push, plus preview builds for each branch.',
    used: u('Medibook, Shopsmart, Bankify, Taskflow, FoodDelivery, E-Book, GuessNumber') },
  { name: 'AWS Basics', category: 'devops', Icon: FaAws, color: '#FF9900',
    description: 'Core cloud services: EC2 for servers, S3 for storage, RDS for databases and IAM for access control.',
    used: u('Taskflow, FoodDelivery') },
  { name: 'IntelliJ IDEA', category: 'devops', Icon: SiIntellijidea, color: '#FE315D',
    description: 'Main IDE for Java and Spring Boot: debugging, refactoring and run configurations.',
    used: u(ALL) },
  { name: 'VS Code', category: 'devops', Icon: SiVscodium, color: '#007ACC',
    description: 'Editor for React and JavaScript work: extensions, debugging and the integrated terminal.',
    used: u(ALL) }
];

const SKILLS = RAW.map((s, i) => ({ id: i + 1, ...s }));

const CAT_LABEL = {
  backend: 'Backend',
  frontend: 'Frontend',
  ai: 'AI / ML',
  database: 'Database',
  devops: 'Tools'
};

const CATEGORIES = [
  { id: 'all', label: 'All Skills', Icon: FaCode },
  { id: 'backend', label: 'Backend', Icon: FaServer },
  { id: 'frontend', label: 'Frontend', Icon: FaReact },
  { id: 'ai', label: 'AI / ML', Icon: FaBrain },
  { id: 'database', label: 'Database', Icon: FaDatabase },
  { id: 'devops', label: 'DevOps & Tools', Icon: FaDocker }
].map((c) => ({
  ...c,
  count: c.id === 'all' ? SKILLS.length : SKILLS.filter((s) => s.category === c.id).length
}));

/* Floating glass cubes (skills): [skill name, left%, top%, size(px), tilt, depth, delay] */
const CUBES = [
  ['React.js', 3, 8, 190, -6, 26, 0],
  ['Java', 20, 26, 125, 7, 34, 1.2],
  ['Python', 6, 40, 140, 5, 30, 0.5],
  ['MySQL', 22, 55, 130, 4, 40, 0.9],
  ['Git & GitHub', 8, 85, 105, -5, 44, 1.1],
  ['Flask', 24, 73, 105, 6, 36, 1.6],
  ['Spring Boot', 80, 5, 205, 5, 18, 0.6],
  ['Node.js', 68, 30, 130, -4, 30, 0.3],
  ['Machine Learning', 86, 34, 150, 6, 24, 0.8],
  ['XGBoost', 70, 52, 105, -5, 38, 1.4],
  ['MongoDB', 82, 58, 130, -7, 22, 1.5],
  ['Redis', 70, 70, 100, -6, 36, 0.7],
  ['Docker', 80, 80, 105, 6, 38, 0.4],
  ['Render', 9, 64, 105, -6, 34, 1.0]
]
  .map(([name, left, top, size, tilt, depth, delay]) => ({
    name, left, top, size, tilt, depth, delay,
    skill: SKILLS.find((k) => k.name === name)
  }))
  .filter((c) => c.skill);

/* Floating cubes (projects): link opens the live project */
const PROJECT_CUBES = [
  { name: 'Bhoomi Seva', sub: 'Live Project', Icon: FaMapMarkedAlt, color: '#F97316', url: 'https://bhoomi-frontend-f5sw.onrender.com/', left: 28, top: 4, size: 150, tilt: -5, depth: 28, delay: 0.2 },
  { name: 'Majhi-Yojna', sub: 'Live Project', Icon: FaHandHoldingHeart, color: '#14B8A6', url: 'https://majhi-yojna-frontend.onrender.com/', left: 45.5, top: 3, size: 150, tilt: 4, depth: 22, delay: 0.7 },
  { name: 'MediBook Pro', sub: 'Live Project', Icon: FaHospital, color: '#00D9A5', url: 'https://medibook-pro-front.onrender.com/', left: 63, top: 4, size: 150, tilt: -4, depth: 26, delay: 1.1 }
];

const HUMAN_IMAGE = '/images/human-back.png';

/* mobile positions: [left %, top %]  (top = stage height ka %) */
const MOBILE_POS = {
  /* Projects — top arch */
  'Bhoomi Seva':   [4, 20.5],
  'Majhi-Yojna':   [40, 20.5],
  'MediBook Pro':  [76, 20.5],

  /* Upper inner  */
  'Python':            [3, 37],
  'Java':              [27, 35],
  'Spring Boot':       [58, 35],
  'Machine Learning':  [82, 37],

  /* Middle inner */
  'MySQL':     [3, 50],
  'React.js':  [27, 49],
  'Node.js':   [58, 49],
  'XGBoost':   [82, 50],

  /* Lower sides */
  'Flask':         [3, 63],
  'MongoDB':       [82, 63],
  'Git & GitHub':  [3, 75],
  'Redis':         [82, 75],
  'Render':        [3, 88],
  'Docker':        [82, 88]
};
const mpos = (name) => {
  const p = MOBILE_POS[name] || [0, 0];
  return { '--ml': `${p[0]}%`, '--mt': `${p[1]}%` };
};

/* particles ek baar bante hain (har render par nahi) */
const PARTICLES = Array.from({ length: 36 }, (_, i) => ({
  key: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: 2 + Math.random() * 5,
  delay: Math.random() * 6,
  dur: 5 + Math.random() * 6
}));

const ease = [0.22, 1, 0.36, 1];
const MAX_TAGS = 5;

/* ------------------------------------------------------------------ */
/*  Skill card                                                         */
/* ------------------------------------------------------------------ */
const SkillCard = memo(function SkillCard({ skill, index }) {
  const [open, setOpen] = useState(false);
  const { Icon } = skill;
  const extra = skill.used.length - MAX_TAGS;
  const shown = open ? skill.used : skill.used.slice(0, MAX_TAGS);

  // mouse ke saath chalti roshni (touch par nahi)
  const onMove = (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  return (
    <motion.article
      className="su-card"
      style={{ '--c': skill.color }}
      initial={{ opacity: 0, y: 38, scale: 0.97 }}
      whileInView={{
        opacity: 1, y: 0, scale: 1,
        transition: { duration: 0.55, delay: (index % 4) * 0.07, ease }
      }}
      viewport={{ once: true, margin: '-40px' }}
      whileHover={{ y: -8, transition: { type: 'spring', stiffness: 300, damping: 22 } }}
      onPointerMove={onMove}
    >
      <span className="su-card-bar" />

      <div className="su-card-head">
        <div className="su-card-ico" style={{ color: skill.color }}><Icon /></div>
        <div className="su-card-id">
          <h3>{skill.name}</h3>
          <span className="su-badge">{CAT_LABEL[skill.category]}</span>
        </div>
      </div>

      <p className="su-desc">{skill.description}</p>

      {skill.used.length > 0 && (
        <div className="su-used">
          <div className="su-used-top">
            <span className="su-used-label">Used in</span>
            <span className="su-count">
              <b>{skill.used.length}</b> {skill.used.length === 1 ? 'project' : 'projects'}
            </span>
          </div>
          <div className="su-tags">
            {shown.map((p) => <span key={p}>{p}</span>)}
            {extra > 0 && (
              <button
                type="button"
                className="su-more"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
              >
                {open ? 'Show less' : `+${extra}`}
              </button>
            )}
          </div>
        </div>
      )}
    </motion.article>
  );
});

/* ------------------------------------------------------------------ */
/*  PAGE                                                               */
/* ------------------------------------------------------------------ */
const Skills = () => {
  const [category, setCategory] = useState('all');
  const stageRef = useRef(null);

  const filtered = useMemo(
    () => (category === 'all' ? SKILLS : SKILLS.filter((s) => s.category === category)),
    [category]
  );

  // parallax CSS variables se chalta hai: React re-render nahi hota
  const onStageMove = useCallback((e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    const el = stageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
    el.style.setProperty('--my', ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
  }, []);

  const onStageLeave = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    el.style.setProperty('--mx', '0');
    el.style.setProperty('--my', '0');
  }, []);

  const jumpTo = (cat) => {
    setCategory(cat);
    const el = document.getElementById('su-explorer');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="su-page">

        {/* ============ HERO SCENE ============ */}
        <section
          className="su-stage"
          ref={stageRef}
          onPointerMove={onStageMove}
          onPointerLeave={onStageLeave}
        >
          <div className="su-glow" />

          {PARTICLES.map((p) => (
            <i
              key={p.key}
              className={`su-particle ${p.key >= 18 ? 'extra' : ''}`}
              style={{
                left: `${p.left}%`, top: `${p.top}%`,
                width: p.size, height: p.size,
                animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`
              }}
            />
          ))}

          {/* neon ribbon */}
          <svg className="su-ribbon" viewBox="0 0 1200 700" preserveAspectRatio="none" aria-hidden="true">
            <path className="su-ribbon-blur" pathLength="1000" d="M150 400 C150 230 450 230 600 400 S1050 570 1050 400 C1050 230 750 230 600 400 S150 570 150 400Z" />
            <path className="su-ribbon-core" pathLength="1000" d="M150 400 C150 230 450 230 600 400 S1050 570 1050 400 C1050 230 750 230 600 400 S150 570 150 400Z" />
          </svg>

          {/* human back image */}
          <div className="su-person" aria-hidden="true">
            <div className="su-ring" />
            <img
              src={HUMAN_IMAGE}
              alt=""
              className="su-human-img"
              decoding="async"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>

          {/* title */}
          <div className="su-heading">
            <motion.p
              className="su-heading-eyebrow"
              initial={{ opacity: 0, letterSpacing: '0.9em' }}
              animate={{ opacity: 1, letterSpacing: '0.5em' }}
              transition={{ duration: 1.2, delay: 0.2 }}
            >
              <i /> SKILLS <i />
            </motion.p>

            <h1 className="su-heading-title">
              {['THE TECH', 'UNIVERSE'].map((line, i) => (
                <motion.span
                  key={line}
                  className="su-heading-line"
                  initial={{ opacity: 0, y: 26, filter: 'blur(10px)', letterSpacing: '0.7em' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)', letterSpacing: '0.3em' }}
                  transition={{ duration: 1.1, delay: 0.5 + i * 0.3, ease: 'easeOut' }}
                >
                  {line}
                </motion.span>
              ))}
            </h1>

            <motion.span
              className="su-heading-beam"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1, delay: 1.3 }}
            />

            <motion.p
              className="su-heading-sub"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.5 }}
            >
              FULL STACK <b>·</b> AI / ML <b>·</b> DEPLOYED
            </motion.p>
          </div>

          {/* floating glass cubes: skills */}
          {CUBES.map(({ name, left, top, size, tilt, depth, delay, skill }) => {
            const { Icon } = skill;
            return (
              <div
                key={name}
                className="su-cube-pos"
                style={{ left: `${left}%`, top: `${top}%`, '--s': size, '--d': depth, ...mpos(name) }}
              >
                <button
                  type="button"
                  className="su-cube"
                  onClick={() => jumpTo(skill.category)}
                  aria-label={`Show ${CAT_LABEL[skill.category]} skills`}
                  style={{ '--c': skill.color, '--tilt': `${tilt}deg`, animationDelay: `${delay}s` }}
                >
                  <Icon className="su-cube-icon" style={{ color: skill.color }} />
                  <span className="su-cube-name">{name}</span>
                  <span className="su-cube-lvl">{CAT_LABEL[skill.category]}</span>
                </button>
              </div>
            );
          })}

          {/* floating glass cubes: projects */}
          {PROJECT_CUBES.map(({ name, sub, Icon, color, url, left, top, size, tilt, depth, delay }) => (
            <div
              key={name}
              className="su-cube-pos is-project"
              style={{ left: `${left}%`, top: `${top}%`, '--s': size, '--d': depth, ...mpos(name) }}
            >
              <a
                className="su-cube su-project"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${name} live project`}
                style={{ '--c': color, '--tilt': `${tilt}deg`, animationDelay: `${delay}s` }}
              >
                <Icon className="su-cube-icon" style={{ color }} />
                <span className="su-cube-name">{name}</span>
                <span className="su-cube-lvl">{sub}</span>
              </a>
            </div>
          ))}

          <div className="su-side-text" aria-hidden="true">
            BETTER<br />INTERFACES<br />A BRIGHTER<br />TOMORROW
          </div>
        </section>

        {/* ============ EXPLORER ============ */}
        <section id="su-explorer" className="su-explorer">
          <motion.div
            className="su-ex-head"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease }}
          >
            <span className="su-ex-eyebrow"><i /> Toolbox <i /></span>
            <h2>Skills <em>&amp;</em> Technologies</h2>
            <p>Everything I use to design, build and ship. Pick a category to filter.</p>
          </motion.div>

          <div className="su-tabs" role="tablist" aria-label="Skill categories">
            {CATEGORIES.map(({ id, label, Icon, count }) => (
              <button
                key={id}
                type="button"
                role="tab"
                title={label}
                aria-label={`${label} (${count})`}
                aria-selected={category === id}
                className={`su-tab ${category === id ? 'on' : ''}`}
                onClick={() => setCategory(id)}
              >
                {category === id && (
                  <motion.span
                    layoutId="su-tab-pill"
                    className="su-tab-pill"
                    transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  />
                )}
                <span className="su-tab-in">
                  <Icon />
                  <span className="su-tab-label">{label}</span>
                  <em>{count}</em>
                </span>
              </button>
            ))}
          </div>

         {(() => {
          const cur = CATEGORIES.find((c) => c.id === category);
          return (
            <p className="su-tab-caption">
              <b>{cur.label}</b> · {cur.count} skills
            </p>
          );
        })()}

          <div key={category} className="su-grid">
            {filtered.map((s, i) => (
              <SkillCard key={s.id} skill={s} index={i} />
            ))}
          </div>
        </section>
      </div>
    </MotionConfig>
  );
};

export default Skills;